import type {
  AllPermissionsResponse,
  FormattedPermission,
  PermissionAttributesWithId,
  TransactionMaxValues
} from '@library/types/permissions';
import type {
  AllRolesResponse,
  Role,
  RoleWithPermissions
} from '@library/types/role';
import type {
  FormattedUser,
  FormattedUsers,
  User,
  UserRoleAttributes,
  UserRoleAttributesRequest,
  UserRoleOption,
  UserStatus,
  UsersResponse
} from '@library/types/user';
import { adjust, head, tail, toUpper, uniq } from 'ramda';
import type { ComboboxOption } from '@library/components/selects';
import { DEFAULT_TRANSACTION_MAX_IDS } from '@bo-constants/roles';
import type { SelectOption } from '@library/types/utils';
import { sortByKey } from '@bo-utils/common';

export const transformUser = (user: User): FormattedUser => {
  const { attributes, id, type } = user;

  return {
    id,
    type,
    ...attributes
  };
};

export const transformUsers = (
  response: UsersResponse | undefined
): FormattedUsers | undefined => {
  if (!response) {
    return undefined;
  }

  const formattedUsers = response.data?.map((user) => transformUser(user));

  return {
    paginationInfo: response.meta,
    users: formattedUsers
  } as FormattedUsers;
};

export const formatPermissionName = (name: string) => {
  return name
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) =>
      ['csv', 'tin'].includes(word)
        ? toUpper(word)
        : toUpper(head(word) || '') + tail(word)
    )
    .join(' ');
};

export const createRoleComboboxOptions = (
  response: AllRolesResponse | undefined
): Array<ComboboxOption> => {
  if (!response?.data) {
    return [];
  }

  const roleOptions = response.data.map((role) => ({
    label: role.attributes.name,
    value: role.id
  }));

  return sortByKey(roleOptions, 'label');
};

export const createRoleSelectOptions = (
  response: AllRolesResponse | undefined
): Array<SelectOption> => {
  if (!response?.data) {
    return [];
  }

  const roleOptions = response.data.map((role) => ({
    name: role.attributes.name,
    value: role.id,
    key: String(role.id)
  }));

  // admin_users endpoint filters users without roles using 'none' value
  const noneOption: SelectOption = {
    name: 'None',
    key: 'none',
    value: 'none'
  };
  roleOptions.push(noneOption);

  return sortByKey(roleOptions, 'name');
};

export const mapUserRolesToValue = (roles: Array<UserRoleAttributes>) =>
  roles.map((role) => ({ label: role.name, value: String(role.role_id) }));

export const mapTransactionMaxValues = (
  permissions: Array<PermissionAttributesWithId>,
  user: FormattedUser
): Array<TransactionMaxValues> => {
  const userPermissionIds = new Set(
    user.permissions
      ?.filter((perm) => perm.has_transaction_limit)
      .map((perm) => perm.id)
  );

  const transactionMaxValues = ({
    id,
    name,
    transaction_limits
  }: PermissionAttributesWithId) => ({
    id: String(id),
    label: formatPermissionName(name),
    value: transaction_limits?.[0]?.dollar_amount?.toString() ?? ''
  });

  return permissions.reduce<Array<TransactionMaxValues>>((acc, perm) => {
    if (userPermissionIds.has(perm.id)) {
      acc.push(transactionMaxValues(perm));
    }
    return acc;
  }, []);
};

export const mapUserRoleOptions = (
  user: FormattedUser,
  leagueOptions: Array<ComboboxOption>
): Array<UserRoleOption> => {
  const { roles } = user;

  return (
    roles.map(
      ({ league_ids, name, permissions, role_id, user_role_id }, index) => {
        const userRoleOption: UserRoleOption = {
          id: String(user_role_id),
          isSettingOpen: index === 0,
          role: {
            label: name ?? '',
            value: String(role_id)
          },
          leagues:
            leagueOptions?.filter((option) =>
              league_ids?.includes(Number(option.value))
            ) ?? [],
          transactionMaxes: mapTransactionMaxValues(permissions, user)
        };

        return userRoleOption;
      }
    ) ?? []
  );
};

export const mapUserRolesAttributes = (
  userRoles: Array<UserRoleOption>
): Array<UserRoleAttributesRequest> => {
  const userRolesRequest = userRoles.map(
    ({ role, leagues, transactionMaxes }) => {
      const roleId = Number(role?.value);
      const leagueIds = leagues?.map((league) => Number(league.value));
      const transactionLimitsAttributes =
        transactionMaxes
          ?.filter((tm) => tm.value !== '')
          ?.map((tm) => ({
            ...(!tm.id
              ? { id: Number(tm.id) }
              : { permission_id: Number(tm.id) }),
            dollar_amount: Number(tm.value)
          })) ?? [];

      return {
        role_id: roleId,
        league_ids: leagueIds,
        ...(!!transactionMaxes?.length && {
          transaction_limits_attributes: transactionLimitsAttributes
        })
      };
    }
  );

  return userRolesRequest;
};

export const deleteUserRolesAttributes = (
  editUserRoles: Array<UserRoleOption>,
  userRoles: Array<UserRoleAttributes>
): Array<UserRoleAttributesRequest> => {
  const deletedUserRoles = (
    acc: Array<UserRoleAttributesRequest>,
    userRole: UserRoleAttributes
  ) => {
    const editUserRole = editUserRoles.find(
      (editUser) => editUser.role?.value === userRole.user_role_id
    );
    // delete role if not present in original userRoles array
    if (!editUserRole) {
      acc.push({
        id: Number(userRole.user_role_id),
        league_ids: [],
        _destroy: true
      });
    }
    return acc;
  };

  return userRoles.reduce(deletedUserRoles, []);
};

export const transformRoleWithPermissions = (
  role: Role
): RoleWithPermissions => {
  const {
    admin_user_count: userCount,
    description,
    name,
    permissions
  } = role.attributes;

  const permissionIds =
    permissions?.flatMap((permission) => String(permission.id)) ?? [];

  const transactionMaxOptions =
    permissions
      ?.filter(({ id }) => DEFAULT_TRANSACTION_MAX_IDS.includes(String(id)))
      ?.map((permission) => {
        const transactionLimitValue =
          permission?.transaction_limits?.find(
            (tl) => tl.permission_id === Number(permission.id)
          )?.dollar_amount ?? '';

        return {
          id: permission.id ?? '',
          label: formatPermissionName(String(permission?.name)) ?? '',
          value: String(transactionLimitValue)
        };
      }) ?? [];

  return {
    id: role.id,
    description,
    name,
    permissionIds,
    transactionMaxOptions,
    userCount: userCount ?? 0
  };
};

export const getRolesWithPermissions = (
  response: AllRolesResponse | undefined
): Array<RoleWithPermissions> => {
  if (!response?.data) {
    return [];
  }

  const rolesWithPermissions = response.data.map((role) =>
    transformRoleWithPermissions(role)
  );

  return sortByKey(rolesWithPermissions, 'name');
};

export const transformPermissions = (
  response: AllPermissionsResponse | undefined
): Array<FormattedPermission> => {
  if (!response?.data) {
    return [];
  }

  return response.data.map((permission) => ({
    id: permission.id,
    hasPermission: false,
    label: formatPermissionName(permission.attributes.name),
    transactionMaxValue: String(
      permission.attributes.transaction_limits?.[0]?.dollar_amount ?? ''
    ),
    ...permission.attributes
  })) as Array<FormattedPermission>;
};

export const getPermissionsByCurrentRoles = (
  currentRoles: Array<ComboboxOption> | null,
  permissionsData: Array<FormattedPermission>,
  rolesWithPermissions: Array<RoleWithPermissions>
) => {
  const currentRoleIds = currentRoles?.map((role) => role.value);
  const currentPermissionIds = uniq(
    rolesWithPermissions
      .filter((role) => currentRoleIds?.includes(role.id))
      .flatMap((role) => role.permissionIds)
  );

  const formattedPermissions: Array<FormattedPermission> = permissionsData?.map(
    (permission) => ({
      ...permission,
      hasPermission: currentPermissionIds.includes(permission.id)
    })
  );

  return sortByKey(formattedPermissions, 'label');
};

export const getAssignedRolesByPermission = (
  currentRoles: Array<ComboboxOption> | null,
  rolesWithPermissions: Array<RoleWithPermissions>,
  permissionId: string
): Array<string> => {
  const currentRolesWithPermission = rolesWithPermissions.filter((role) =>
    currentRoles?.some((currentRole) => currentRole.value === role.id)
  );

  return currentRolesWithPermission
    .filter((role) => role.permissionIds.includes(permissionId))
    .map((role) => role.name);
};

export const createPermissionComboboxOptions = (
  response: Array<FormattedPermission> | undefined
): Array<ComboboxOption> => {
  if (!response) {
    return [];
  }

  const permissionOptions = response.map(({ id, label }) => ({
    label,
    value: id
  }));

  return sortByKey(permissionOptions, 'label');
};

export const getTransactionMaxByPermissionValues = (
  permValues: Array<ComboboxOption> | null
) => {
  const transactionMaxValues = permValues
    ?.filter(({ value }) => DEFAULT_TRANSACTION_MAX_IDS.includes(String(value)))
    .map(({ label, value }) => ({
      id: String(value),
      label,
      value: ''
    })) as Array<TransactionMaxValues>;

  return transactionMaxValues;
};

export const getTransactionMaxOptionsByRoleId = (
  rolesWithPermissions: Array<RoleWithPermissions> | undefined,
  roleId: string
): Array<TransactionMaxValues> =>
  rolesWithPermissions?.find((rwp) => rwp.id === roleId)
    ?.transactionMaxOptions ?? [];

export const formatUserDetails = (user: FormattedUser) => {
  const {
    deactivated,
    email,
    first_name: firstName,
    id,
    last_name: lastName,
    google_avatar: avatar,
    roles,
    created_at: createdAt
  } = user;

  const name = firstName && lastName ? `${firstName} ${lastName}` : '';
  const initials = email ? email[0] : '';
  const status: UserStatus = deactivated ? 'deactivated' : 'active';

  return {
    avatar,
    createdAt,
    email,
    id,
    initials,
    name,
    roles,
    status
  };
};

export const replaceUserById = (
  user: User,
  formattedUsers: Array<FormattedUser>
) => {
  const updatedUser = transformUser(user);

  const selectedUserIndex =
    formattedUsers.findIndex((forUser) => forUser.id === updatedUser.id) ?? -1;

  const updatedUsers = adjust(
    selectedUserIndex,
    () => updatedUser,
    formattedUsers
  );

  return { updatedUser, updatedUsers };
};
